import { useEffect, useRef, useState } from "react";
import { Editor } from "primereact/editor";
import API from "../../api";
import { showError, showSuccess } from "../../utils/dialog";

const EditMessageTemplateForm = ({ templateId, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const previewWinRef = useRef(null);
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const resp = await API.get(`/api/admin/messages/templates/${templateId}`);
        const t = resp.data?.data || {};
        setName(t.name || "");
        setContent(t.content || "");
      } catch (err) {
        console.error("Failed to load template", err);
        showError("Failed to load template");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [templateId]);

  useEffect(() => {
    const editor = editorRef.current?.getQuill?.();
    if (!editor) return;
    const toolbar = editor.getModule("toolbar");
    if (!toolbar) return;
    const handleImage = () => {
      if (imageInputRef.current) imageInputRef.current.click();
    };
    toolbar.addHandler("image", handleImage);
  }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    setIsUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await API.post("/api/admin/messages/templates/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!resp.data?.success || !resp.data?.data?.url) {
        throw new Error(resp.data?.message || "Upload failed");
      }
      const url = resp.data.data.url;
      const editor = editorRef.current?.getQuill?.();
      if (editor) {
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        editor.insertEmbed(index, "image", url, "user");
        editor.setSelection(index + 1);
      }
      showSuccess("Image uploaded");
    } catch (err) {
      console.error("Image upload failed", err);
      showError("Failed to upload image");
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const saveTemplate = async (closeAfter) => {
    if (!name.trim()) {
      showError("Template name is required");
      return;
    }
    setIsSaving(true);
    try {
      const payload = { name: name.trim(), content: content || "", styles: "" };
      if (templateId) {
        await API.put(`/api/admin/messages/templates/${templateId}`, payload);
        showSuccess("Template saved");
      } else {
        const resp = await API.post(`/api/admin/messages/templates`, payload);
        if (!resp.data?.success) {
          throw new Error(resp.data?.message || "Failed to create template");
        }
        showSuccess("Template created");
      }
      onSaved && onSaved();
      if (closeAfter) onClose && onClose();
    } catch (err) {
      console.error("Save template error", err);
      showError("Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  const previewTemplate = async () => {
    try {
      const resp = await API.post("/api/admin/messages/templates/preview", {
        content,
        styles: "",
      });
      if (!resp.data?.success) {
        throw new Error(resp.data?.message || "Preview failed");
      }
      const html = resp.data?.data?.html || "";
      if (!previewWinRef.current || previewWinRef.current.closed) {
        const width = 700;
        const height = 600;
        const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
        const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
        const features = `menubar=0,toolbar=0,location=0,status=0,resizable=1,scrollbars=1,width=${width},height=${height},left=${left},top=${top}`;
        previewWinRef.current = window.open("", "templatePreview", features);
      }
      const w = previewWinRef.current;
      if (w) {
        const previewName = String(name || "").trim() || "Untitled Template";
        const escapedName = previewName
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

        w.document.open();
        w.document.write(
          `<!doctype html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1" /><title>${escapedName} - Preview</title></head><body><div style="font-family: Inter, Arial, Helvetica, sans-serif; padding:12px 16px; border-bottom:1px solid #eee;"><h3 style="margin:0; font-size:18px; color:#222;">${escapedName}</h3></div><div style="padding:12px;">${html}</div></body></html>`
        );
        w.document.close();
        w.focus();
      }
    } catch (err) {
      console.error("Preview error", err);
      showError("Failed to preview template");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-[95%] sm:w-full max-w-4xl p-6 relative flex flex-col max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
        >
          ✖
        </button>

        <h2 className="text-xl text-formTitleText font-semibold mb-2">
          {templateId ? "Edit Template" : "Create Template"}
        </h2>

        <div className="h-[1px] bg-[#DC1F004D]"></div>

        {isLoading ? (
          <div className="py-6 text-sm text-gray-500">Loading...</div>
        ) : (
          <>
            <label className="block text-sm text-textGray mb-2 mt-4">Name <span className="text-[#00000080]">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mb-3 focus:ring-1 focus:ring-red-400"
            />

            <label className="block text-sm text-textGray mb-2">Content</label>
            <Editor
              ref={editorRef}
              value={content}
              onTextChange={(e) => setContent(e.htmlValue)}
              style={{ height: "260px", marginBottom: "8px" }}
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadImage(e.target.files?.[0])}
            />
            {isUploadingImage && (
              <div className="text-xs text-gray-500">Uploading image...</div>
            )}
          </>
        )}

        <div className="flex items-center justify-between gap-4 mt-2">
          <button
            onClick={onClose}
            className="w-1/4 border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-3 py-2 rounded-full"
          >
            CANCEL
          </button>
          <button
            onClick={() => saveTemplate(false)}
            disabled={isSaving}
            className="w-1/4 bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-3 py-2 rounded-full"
          >
            {isSaving ? "SAVING..." : "SAVE"}
          </button>
          <button
            onClick={() => saveTemplate(true)}
            disabled={isSaving}
            className="w-1/4 bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-3 py-2 rounded-full"
          >
            {isSaving ? "SAVING..." : "SAVE & CLOSE"}
          </button>
          <button
            onClick={previewTemplate}
            className="w-1/4 border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-3 py-2 rounded-full"
          >
            PREVIEW
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMessageTemplateForm;
